import type { ReactNode } from 'react';
import cn from 'classnames';
import styles from './Bubble.module.scss';
import Markdown from './Markdown';
import { handleActions } from '@chewy/chat-library-common';
import { CAI_CTA_PROTOCOL } from './markdownConstants';
import { ActionDef } from '@chewy/chat-library-common';

type Responder = 'user' | 'aiBot' | 'agent';
export type BubbleVariant = 'default' | 'typing';

export interface BubbleProps {
  responder: Responder;
  variant?: BubbleVariant;
  children: ReactNode;
}

const handleExternalLink = (url: string) => {
  handleActions({
    type: 'link',
    text: url,
    url,
  });
};

function renderStringChildren(
  text: string,
  onMarkdownClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
) {
  return <Markdown onClick={onMarkdownClick}>{text}</Markdown>;
}

export default function Bubble({ responder, variant = 'default', children }: BubbleProps) {
  const cls = responder === 'user' ? styles.user : responder === 'agent' ? styles.agent : styles.aiBot;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    let ctaUrl: URL;

    try {
      ctaUrl = new URL(e.currentTarget.href);
    } catch (error) {
      console.error('Error parsing URL', error);
      return;
    }

    const actionType = ctaUrl.searchParams.get('ctaType');
    const orderId = ctaUrl.searchParams.get('orderID');

    if (actionType && ctaUrl.protocol === CAI_CTA_PROTOCOL) {
      ctaUrl.searchParams.delete('ctaType');

      const actionUrl = `${ctaUrl.pathname}${ctaUrl.search}${ctaUrl.hash}`;

      const action: ActionDef = {
        ctaType: actionType,
        text: actionType,
        type: 'link',
        url: actionUrl,
      };

      // Down stream services require us to send widget data
      // and since the action links don't have that we have
      // to construct it from the data we do have
      let widgetData;
      if (orderId && actionType === 'go_to_order_details') {
        widgetData = {
          orderId,
          cardType: 'ORDER',
          actions: [
            action
          ]
        };
      }

      handleActions(action, widgetData);

      return;
    }

    if (ctaUrl.protocol === 'http:' || ctaUrl.protocol === 'https:') {
      handleExternalLink(ctaUrl.href);
    }
  };

  return (
    <div className={`${styles.bubbleWrapper} ${cls}`}>
      <div className={cn(styles.bubble, {
        [styles.markdown]: typeof children === 'string',
        [styles.typing]: variant === 'typing',
      })}>
        {typeof children === 'string'
          ? renderStringChildren(children, handleClick)
          : children}
      </div>
    </div>
  );
}
